import React, { useCallback, useRef } from 'react';
import { PanelResizeHandle } from 'react-resizable-panels';
import { cn } from '../../lib/utils';

/**
 * PaneHandle — the boundary between two panes of a resizable console.
 *
 * It is 12px, which is `md:gap-3`, which is the gap the project Layout puts
 * between every top-level surface. That equality is the whole idea: a boundary
 * inside the console looks exactly like a boundary between floating surfaces,
 * so the ambient canvas showing through the seam *is* the drag target. Nothing
 * is drawn until you approach it.
 *
 * Percentages never have to account for this width. Panels are laid out with
 * `flex-basis: 0; flex-grow: <size>`, so flexbox subtracts every handle from the
 * free space before splitting what's left — which also means the group must NOT
 * carry a `gap`, or the seam would be 24px.
 *
 * Three things here are not obvious, and all three are forced by the library:
 *
 *  - `hitAreaMargins` is far below the {coarse:15, fine:5} default. Inside that
 *    margin react-resizable-panels sets `*{cursor: col-resize !important}` on
 *    the whole document and stopImmediatePropagation()s clicks — so a generous
 *    margin quietly makes every control near a pane edge feel broken. 12px of
 *    real handle is already a big target; it doesn't need help.
 *  - The state comes from `data-resize-handle-state`, not `:hover`. The
 *    library's notion of hover is hit-area geometry, so CSS hover disagrees
 *    with it by up to `hitAreaMargins` px and the grip would flicker.
 *  - Double-click is synthesised from two onClicks. The library preventDefault()s
 *    pointerdown, which suppresses the compatibility dblclick event; its own
 *    `onClick` (derived from pointer tracking, fired only when the pointer
 *    didn't move) is the only reliable signal.
 */

// Long enough to be forgiving, short enough that two deliberate separate clicks
// on a handle — which do nothing anyway — don't read as a reset.
const DOUBLE_CLICK_MS = 350;

export function PaneHandle({ onReset, className, ...rest }) {
    const lastClick = useRef(0);

    const handleClick = useCallback(() => {
        const now = Date.now();
        if (now - lastClick.current < DOUBLE_CLICK_MS) {
            lastClick.current = 0;
            onReset?.();
            return;
        }
        lastClick.current = now;
    }, [onReset]);

    return (
        <PanelResizeHandle
            onClick={handleClick}
            hitAreaMargins={{ coarse: 12, fine: 2 }}
            title={onReset ? 'Drag to resize · double-click to reset' : 'Drag to resize'}
            className={cn(
                // shrink-0: the library sets no flex properties on the handle, so
                // it would inherit `flex-shrink: 1` and collapse below 12px in a
                // tight container — taking the seam with it.
                'group relative shrink-0 outline-none',
                'w-3 cursor-col-resize',
                // A `w-3` handle in a column has `height: auto` — i.e. zero. The
                // axis has to be swapped explicitly for vertical groups.
                'data-[panel-group-direction=vertical]:h-3',
                'data-[panel-group-direction=vertical]:w-auto',
                'data-[panel-group-direction=vertical]:cursor-row-resize',
                className
            )}
            {...rest}
        >
            {/* The grip. Same vocabulary as ArtifactSurface's handle, so the two
                resize idioms in the app read as one. */}
            <div
                aria-hidden
                className={cn(
                    'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full',
                    'h-16 w-1 bg-transparent transition-colors duration-150',
                    'group-data-[panel-group-direction=vertical]:h-1',
                    'group-data-[panel-group-direction=vertical]:w-16',
                    'group-data-[resize-handle-state=hover]:bg-primary/50',
                    'group-data-[resize-handle-state=drag]:bg-primary',
                    'group-focus-visible:bg-primary/70'
                )}
            />
        </PanelResizeHandle>
    );
}

export default PaneHandle;
